import React from "react";

import {
  Grid,
  Skeleton,
  Stack,
  Typography,
  useTheme,
  alpha,
} from "@mui/material";

interface OrganizationDetailsSkeletonProps {
  organizationName?: string;
}

const OrganizationDetailsSkeleton = ({
  organizationName = "Organization",
}: OrganizationDetailsSkeletonProps) => {
  const theme = useTheme();
  const cardSx = {
    borderRadius: 2,
    border: `1px solid ${alpha(theme.palette.divider, 0.06)}`,
    p: 2,
    bgcolor: "background.paper",
  };

  return (
    <Stack spacing={3}>
      <Typography
        component="h1"
        sx={{
          position: "absolute",
          width: 1,
          height: 1,
          p: 0,
          m: -1,
          overflow: "hidden",
          clip: "rect(0 0 0 0)",
          whiteSpace: "nowrap",
          border: 0,
        }}
      >
        {organizationName}
      </Typography>
      <Stack sx={cardSx} spacing={1.5}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          gap={2}
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Skeleton variant="text" width={220} height={40} />
            <Skeleton variant="rounded" width={72} height={24} />
          </Stack>
          <Stack direction="row" spacing={1}>
            <Skeleton variant="rounded" width={120} height={36} />
            <Skeleton variant="rounded" width={170} height={36} />
          </Stack>
        </Stack>
        <Skeleton variant="text" width="65%" height={20} />
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          bgcolor: alpha(theme.palette.primary.main, 0.02),
          borderRadius: 1,
          p: 1,
          overflow: "hidden",
        }}
      >
        {Array.from({ length: 9 }).map((_, index) => (
          <Skeleton
            key={index}
            variant="text"
            width={index === 7 ? 100 : 72}
            height={24}
            sx={{ flexShrink: 0 }}
          />
        ))}
      </Stack>

      <Grid container spacing={2}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Stack sx={cardSx} spacing={0.5}>
              <Skeleton variant="text" width={56} height={32} />
              <Skeleton variant="text" width={86} height={20} />
              <Skeleton variant="text" width={120} height={16} />
            </Stack>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2}>
        {Array.from({ length: 4 }).map((_, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Stack sx={cardSx} spacing={1.5}>
              <Skeleton variant="text" width={150} height={24} />
              {Array.from({ length: index === 1 ? 4 : 5 }).map(
                (_, rowIndex) => (
                  <Skeleton
                    key={rowIndex}
                    variant="text"
                    width={`${70 + ((rowIndex * 13) % 30)}%`}
                    height={20}
                  />
                ),
              )}
            </Stack>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
};

export default OrganizationDetailsSkeleton;
